import { TableCell } from '@/components/ui/table';
import { fixedRateTerms, yieldToCall } from '@/components/FixedRateAssetCard';

// Oct 2026 — Screener "Preferred Shares" column cells: Par · Coupon ·
// vs Par · Call Date · Type. Shares the term math with FixedRateAssetCard.
function fmtShort(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const chip = 'inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide border whitespace-nowrap';

export default function PreferredCells({ stock }) {
  const t = fixedRateTerms(stock);
  const diff = t.px > 0 ? t.px - t.par : null;
  const types = [];
  if (stock.is_treasury_vehicle) types.push({ label: 'Treasury', cls: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border-indigo-300/60 dark:border-indigo-700/60' });
  if (stock.preferred_cumulative === true) types.push({ label: 'Cumulative', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60' });
  if (stock.preferred_cumulative === false) types.push({ label: 'Non-Cum', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60' });
  if (t.floating) types.push({ label: 'Fixed→Float', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60' });
  if (t.variable) types.push({ label: 'Variable', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60' });
  if (t.termYear) types.push({ label: `Term ${t.termYear}`, cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60' });

  let call;
  if (t.issuerChoice) {
    call = <span className={`${chip} ${t.abovePar ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-400/60' : 'bg-muted text-muted-foreground border-border'}`} title="Redeemable at par at any time on 15–60 business-days notice — no fixed call date.">Issuer&apos;s choice</span>;
  } else if (t.callPast) {
    call = <span className={`${chip} ${t.abovePar ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border-red-400/60' : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-400/60'}`} title={`Call protection ended ${t.callIso}.`}>{t.abovePar ? '⚠ ' : ''}Callable now</span>;
  } else if (t.callIso) {
    call = <span className="font-mono text-sm" title={`Call protection until ${t.callIso}.`}>{fmtShort(t.callIso)}</span>;
  } else {
    call = (
      <a
        href={`https://www.sec.gov/edgar/search/#/q=%22${encodeURIComponent(stock.ticker || '')}%22&forms=424B2%2C424B5%2C424B4%2C424B3%2C424B7%2CS-3ASR%2CFWP`}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-[10px] text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground whitespace-nowrap"
        title="Call date not yet confirmed — open the SEC EDGAR prospectus search for this ticker to look it up."
        data-testid={`screener-pref-edgar-${stock.ticker}`}
      >
        EDGAR ↗
      </a>
    );
  }

  return (
    <>
      <TableCell className="text-right font-mono text-sm" data-testid={`screener-pref-par-${stock.ticker}`}>
        {t.parKnown ? `$${t.par.toFixed(2)}` : <span className="text-muted-foreground/50">—</span>}
      </TableCell>
      <TableCell className="text-right font-mono text-sm" data-testid={`screener-pref-coupon-${stock.ticker}`}>
        {t.coupon ? `${(t.coupon * 100).toFixed(2)}%` : <span className="text-muted-foreground/50">—</span>}
      </TableCell>
      <TableCell className="text-right font-mono text-sm" data-testid={`screener-pref-vs-par-${stock.ticker}`}>
        {diff == null || !t.parKnown ? <span className="text-muted-foreground/50">—</span>
          : Math.abs(diff) < 0.005 ? <span className="text-muted-foreground">at par</span>
            : <span className={diff > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>{diff > 0 ? '+' : '−'}${Math.abs(diff).toFixed(2)}</span>}
      </TableCell>
      <TableCell className="text-right" data-testid={`screener-pref-call-${stock.ticker}`}>{call}</TableCell>
      <TableCell data-testid={`screener-pref-type-${stock.ticker}`}>
        <div className="flex flex-wrap gap-1">
          {types.length ? types.map((x) => <span key={x.label} className={`${chip} ${x.cls}`}>{x.label}</span>) : <span className="text-muted-foreground/50">—</span>}
        </div>
      </TableCell>
    </>
  );
}

// Yield-to-Call cell — rendered right after the Yield column.
export function PreferredYtcCell({ stock }) {
  const t = fixedRateTerms(stock);
  // Prefer the server-computed figure (what the column sorts on); fall
  // back to the live-price client calc when the server has none.
  const ytc = stock.pref_ytc_pct != null && stock.pref_ytc_mode
    ? { mode: stock.pref_ytc_mode, pct: Number(stock.pref_ytc_pct), years: yieldToCall(stock, t)?.years ?? 0 }
    : yieldToCall(stock, t);
  const divRate = Number(stock.dividend_rate) || 0;
  const cur = t.px > 0 && divRate > 0 ? (divRate / t.px) * 100 : null;
  let body;
  if (!ytc) {
    body = <span className="text-muted-foreground/50" title="No confirmed call date — yield to call needs one.">—</span>;
  } else if (ytc.mode === 'now') {
    body = (
      <span
        className={`font-mono text-sm font-medium ${ytc.pct < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
        title={`Callable now. If the issuer redeems at $${t.par.toFixed(2)} today you ${ytc.pct < 0 ? 'lose' : 'gain'} ${Math.abs(ytc.pct).toFixed(2)}% on today's price — no coupons left to offset it.`}
      >
        {ytc.pct >= 0 ? '+' : ''}{ytc.pct.toFixed(2)}% <span className="text-[9px] uppercase opacity-70">now</span>
      </span>
    );
  } else {
    const worse = cur != null && ytc.pct < cur;
    body = (
      <span
        className={`font-mono text-sm font-medium ${worse ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}
        title={`Yield to call ≈ (annual dividend + (par − price) ÷ ${ytc.years.toFixed(1)} yrs) ÷ average of par and price. ${worse ? 'Below current yield — the above-par premium is lost at the call.' : 'At or above current yield — buying below par adds a pull-to-par gain.'}`}
      >
        {ytc.pct.toFixed(2)}%
      </span>
    );
  }
  return <TableCell className="text-right" data-testid={`screener-pref-ytc-${stock.ticker}`}>{body}</TableCell>;
}
